import React, { JSX, useEffect, useState } from 'react';
import Icons from 'components/atoms/Icons';
import { useFilter } from 'components/context/FilterContext';
import constant from 'assets/utils/constant';
import { getMapsLink } from './LocationMap';

type LocationSidebarProps = {
  img?: {
    value?: {
      alt?: string;
      height?: string;
      src?: string;
      thumbnailsrc?: string;
      width?: string;
    };
  };
  locationName?: string;
  street1?: string;
  street2?: string;
  suite?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  latitude?: string;
  longitude?: string;
};

const LocationWidget = (props: LocationSidebarProps): JSX.Element => {
  const {
    img,
    locationName,
    street1,
    street2,
    suite,
    city,
    state,
    zip,
    phone,
    latitude,
    longitude,
  } = props;

  const [mapUrl, setMapUrl] = useState('');
  const { filterState } = useFilter();
  const userLocationContext = filterState.userLocation;

  const azureMapsKey = process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

  const buildAzureStaticMapUrl = (
    latitude?: string,
    longitude?: string
  ): string => {
    if (!azureMapsKey || !latitude || !longitude) {
      return '';
    }

    const center = `${longitude},${latitude}`;

    // Public custom marker image
    const markerUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${constant.api.azureMapsMarkerUrl}`
      : '';

    // Azure custom pin syntax
    const isLocalhost =
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost';

    const pins = isLocalhost
      ? `default|co7ED957||${longitude} ${latitude}`
      : `custom||${longitude} ${latitude}||${markerUrl}`;

    return `https://atlas.microsoft.com/map/static?api-version=2024-04-01&subscription-key=${encodeURIComponent(
      azureMapsKey
    )}&tilesetId=microsoft.base.road&zoom=15&width=500&height=233&center=${encodeURIComponent(
      center
    )}&pins=${encodeURIComponent(pins)}`;
  };

  useEffect(() => {
    const url = buildAzureStaticMapUrl(latitude, longitude);
    setMapUrl(url);
  }, [latitude, longitude, azureMapsKey]);

  const directionsHref = `https://www.google.com/maps/dir/${
    userLocationContext?.latitude && userLocationContext.latitude !== 0
      ? userLocationContext.latitude
      : ''
  }${userLocationContext?.latitude ? ',' : ''}${
    userLocationContext?.longitude && userLocationContext.longitude !== 0
      ? userLocationContext.longitude
      : ''
  }/${street1 ?? ''}${street2 ? ', ' + street2 : ''},${city ?? ''},${
    state ?? ''
  }+${zip ?? ''}`.replaceAll(' ', '+');

  return (
    <div className="location-widget">
      {mapUrl && (
        <div
          className="location-map"
          itemScope
          itemType="http://schema.org/Hospital"
        >
          <a
            className="track-link-click"
            itemProp="hasMap"
            href={directionsHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              width={500}
              height={233}
              src={mapUrl}
              alt={img?.value?.alt || 'Image of Location on Map'}
            />
          </a>
        </div>
      )}

      <p className="location-name">{locationName}</p>

      <ul className="details">
        {street1 && (
          <li>
            <Icons id="icon-location-new" />
            <a
              className="address-link"
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
              href={getMapsLink({
                street1,
                street2,
                suite,
                city,
                state,
                zip,
              })}
              target="_blank"
              rel="noopener noreferrer"
            >
            {street1}
            {street2 ? `, ${street2}` : ''}
            {suite ? `, ${suite}` : ''}
            {city ? `, ${city}` : ''}
            {state ? `, ${state}` : ''}
            {zip ? ` ${zip}` : ''}
          </a>
          </li>
        )}

        {phone && (
          <li>
            <Icons id="icon-phone-new" />

            <a
              className="phone-link"
              itemScope
              itemType="http://schema.org/Hospital"
              href={`tel:${phone}`}
              itemProp="telephone"
            >
              Call: {phone}
            </a>
          </li>
        )}
      </ul>

      <a
        href={directionsHref}
        className="btn-link btn-primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        Directions
      </a>
    </div>
  );
};

export default LocationWidget;